import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import * as Api from '@vi-iot/client';

import {
  DashboardFilterName,
  DashboardPaidPackagesEnum,
  FeatureNames,
  OptimizationRecommendationsType,
} from '@vi-iot/client';
import { InstallationDetailsStore } from '../installation-details';

import { ViguideRecommendationActions } from './viguide-recommendations.actions';
import {
  optimizationRecommendationsKey,
  IgnoredOptimizationRecommendationTileSettings,
} from './viguide-recommendations.model';

@Injectable()
export class ViguideRecommendationsEffects {
  getViguideRecommendations$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ViguideRecommendationActions.getOptimizationRecommendationsAttempt),
      switchMap(() =>
        this.api.getOptimizationRecommendations().pipe(
          // Remove this switchMap after fixing recommendation on DL
          switchMap((recommendations) =>
            forkJoin(
              recommendations.data.map((recommendation) => {
                if (
                  recommendation.recommendations.some(
                    ({ name }) => name === OptimizationRecommendationsType.dhwSchedule
                  )
                ) {
                  return this.operationalDataApi
                    .getDeviceFeature(
                      recommendation.installationId,
                      recommendation.gatewayId,
                      '0',
                      FeatureNames.dhw
                    )
                    .pipe(
                      map((feature) => {
                        if (!feature.data.isEnabled) {
                          return {
                            ...recommendation,
                            recommendations: recommendation.recommendations.filter(
                              ({ name }) => name !== OptimizationRecommendationsType.dhwSchedule
                            ),
                          };
                        }

                        return recommendation;
                      }),
                      catchError((_) => of(recommendation))
                    );
                } else {
                  return of(recommendation);
                }
              })
            ).pipe(
              map((filteredRecommendations) =>
                filteredRecommendations.filter(
                  (recommendation) => recommendation.recommendations.length !== 0
                )
              )
            )
          ),
          switchMap((recommendations) => [
            ViguideRecommendationActions.getOptimizationRecommendationsSuccess({
              optimizationRecommendations: recommendations,
            }),
            ViguideRecommendationActions.getIgnoredRecommendationTilesSettings(),
          ]),
          catchError((err) =>
            of(ViguideRecommendationActions.getOptimizationRecommendationsError(err))
          )
        )
      )
    );
  });

  getRecommendationTilesSettings$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ViguideRecommendationActions.getIgnoredRecommendationTilesSettings),
      map(() => {
        const optimizationRecommendationsKeyFromLocalStorage = localStorage.getItem(
          optimizationRecommendationsKey
        );

        const ignoredTilesSettings: IgnoredOptimizationRecommendationTileSettings = optimizationRecommendationsKeyFromLocalStorage
          ? JSON.parse(optimizationRecommendationsKeyFromLocalStorage)
          : {};

        return ViguideRecommendationActions.updateIgnoredRecommendationTilesSettings({
          ignoredTilesSettings,
        });
      })
    );
  });

  closeRecommendationTile$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ViguideRecommendationActions.closeRecommendationTile),
      concatLatestFrom(() => this.installationDetailsStore.device$),
      map(([action, device]) => {
        const optimizationRecommendations = localStorage.getItem(optimizationRecommendationsKey);
        const localStorageSettings: IgnoredOptimizationRecommendationTileSettings = optimizationRecommendations
          ? JSON.parse(optimizationRecommendations)
          : {};

        const updatedSerialConfig: string[] = localStorageSettings[device.boilerSerial] ?? [];

        const updatedTilesSettings = {
          ...localStorageSettings,
          [device.boilerSerial]: [...updatedSerialConfig, action.tileName],
        };

        localStorage.setItem(optimizationRecommendationsKey, JSON.stringify(updatedTilesSettings));

        return ViguideRecommendationActions.updateIgnoredRecommendationTilesSettings({
          ignoredTilesSettings: updatedTilesSettings,
        });
      })
    );
  });

  fetchProInstallations$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ViguideRecommendationActions.fetchProInstallations.attempt),
      switchMap(() => {
        const filter = {
          [DashboardFilterName.PaidPackage]: [DashboardPaidPackagesEnum.Pro],
        };
        const limit = Api.MAXIMUM_DASHBOARD_LIMIT;

        return this.dashboardApi
          .instance()
          .getFilteredInstallationsV2(limit, filter)
          .pipe(
            this.dashboardApi.instance().fetchInvitationsRecursively(filter, limit),
            map((installations) =>
              ViguideRecommendationActions.fetchProInstallations.success({ installations })
            ),
            catchError((error) =>
              of(ViguideRecommendationActions.fetchProInstallations.error({ error }))
            )
          );
      })
    );
  });

  constructor(
    private api: Api.AnalyticsApi,
    private actions$: Actions,
    private installationDetailsStore: InstallationDetailsStore,
    private operationalDataApi: Api.OperationalDataApi,
    private dashboardApi: Api.DashboardApiPicker
  ) {}
}
